import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import PageHero from "../components/PageHero";
import { submitEnquiry } from "@/services/enquiry";

const TOPICS = [
  "Villa Investment Enquiry",
  "Site Visit Request",
  "Resort Management Services",
  "Partnership Opportunity",
  "Franchise / Multi-Villa Development",
  "Something else",
];

export default function Contact() {
  const [searchParams] = useSearchParams();
  const villa = searchParams.get("villa");
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [topic, setTopic] = useState(
    villa ? "Villa Investment Enquiry" : ""
  );
  const [message, setMessage] = useState(
    villa
      ? `I'm interested in the ${villa} villa model. Please share details, returns and site visit options.`
      : ""
  );

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get("contact_alt") as string) || "";
    if (honeypot.trim() !== "") {
      setStatus("success");
      return;
    }

    const payload = {
      full_name: String(formData.get("name") || "").trim(),
      email: String(formData.get("email") || "").trim() || undefined,
      phone: String(formData.get("phone") || "").trim() || undefined,
      message: String(formData.get("message") || "").trim() || undefined,
      investment_type: String(formData.get("topic") || "") || undefined,
      page: "contact",
    };

    const result = await submitEnquiry(payload);
    if (result.ok) {
      setStatus("success");
      setFormError("");
      form.reset();
    } else {
      setStatus("error");
      setFormError(result.message);
    }
  }

  return (
    <div className="w-full">
      <PageHero
        eyebrow="Contact Us"
        title={
          <>
            Let's Find Your <span className="italic text-accent-300">Perfect Villa</span>
          </>
        }
        subtitle="Reach out for a free consultation. Our villa investment advisors will get back to you within 24 hours."
      />

      <section className="w-full py-12 md:py-16 bg-background-50">
        <div className="w-full px-4 md:px-8 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-14 items-start">
          <div>
            <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase text-primary-600">
              <i className="ri-mail-send-line"></i> Get In Touch
            </span>
            <h2 className="font-heading text-3xl md:text-4xl text-foreground-950 mt-4 leading-tight">
              Talk to an <span className="italic text-primary-700">Investment Advisor</span>
            </h2>
            <p className="text-foreground-600 text-sm md:text-base mt-4 leading-relaxed">
              Whether you have a specific investment goal or just want to explore your options,
              our team is ready to help you find the right villa and revenue model.
            </p>

            <div className="mt-8 space-y-4">
              <a
                href="tel:8088330330"
                className="flex items-center gap-4 rounded-2xl bg-background-100 border border-background-200 p-4 hover:border-primary-300 transition-colors"
              >
                <span className="flex items-center justify-center w-11 h-11 rounded-full bg-primary-100 text-primary-700">
                  <i className="ri-phone-fill text-xl"></i>
                </span>
                <span>
                  <span className="block text-[11px] uppercase tracking-wide text-foreground-600">Call / WhatsApp</span>
                  <span className="block text-base font-bold text-foreground-950">80883 30330</span>
                </span>
              </a>
              <a
                href="mailto:info@govianu.com"
                className="flex items-center gap-4 rounded-2xl bg-background-100 border border-background-200 p-4 hover:border-primary-300 transition-colors"
              >
                <span className="flex items-center justify-center w-11 h-11 rounded-full bg-primary-100 text-primary-700">
                  <i className="ri-mail-line text-xl"></i>
                </span>
                <span>
                  <span className="block text-[11px] uppercase tracking-wide text-foreground-600">Email</span>
                  <span className="block text-base font-bold text-foreground-950">info@govianu.com</span>
                </span>
              </a>
              <div className="flex items-center gap-4 rounded-2xl bg-background-100 border border-background-200 p-4">
                <span className="flex items-center justify-center w-11 h-11 rounded-full bg-primary-100 text-primary-700">
                  <i className="ri-map-pin-2-line text-xl"></i>
                </span>
                <span>
                  <span className="block text-[11px] uppercase tracking-wide text-foreground-600">Head Office</span>
                  <span className="block text-base font-bold text-foreground-950">#385, 13th Cross Road, Beside Bank of Baroda, Sadashivnagar, Aramane Nagar, Bengaluru – 560080, Karnataka.</span>
                </span>
              </div>
              <div className="flex items-center gap-4 rounded-2xl bg-background-100 border border-background-200 p-4">
                <span className="flex items-center justify-center w-11 h-11 rounded-full bg-primary-100 text-primary-700">
                  <i className="ri-time-line text-xl"></i>
                </span>
                <span>
                  <span className="block text-[11px] uppercase tracking-wide text-foreground-600">Response Time</span>
                  <span className="block text-base font-bold text-foreground-950">Within 24 hours</span>
                </span>
              </div>
            </div>
          </div>

          <div className="rounded-3xl bg-background-100 border border-background-200 p-5 md:p-8">
            <h3 className="font-heading text-2xl text-foreground-950">Send Us a Message</h3>
            <p className="text-sm text-foreground-600 mt-1">
              Tell us about your investment goals and we will guide you to the right villa.
            </p>

            {status === "success" ? (
              <div className="mt-6 rounded-2xl bg-primary-50 border border-primary-200 p-6 text-center">
                <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary-100 text-primary-700">
                  <i className="ri-check-line text-2xl"></i>
                </span>
                <p className="font-heading text-xl text-foreground-950 mt-4">Thank You!</p>
                <p className="text-sm text-foreground-600 mt-2">
                  Your message has been received. Our investment team will contact you within 24
                  hours to discuss your villa options.
                </p>
                <button
                  onClick={() => setStatus("idle")}
                  className="mt-5 text-sm font-semibold text-primary-700 hover:text-primary-800"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-readdy-form className="mt-6 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-xs font-semibold text-foreground-800 mb-1.5">
                      Full Name
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      placeholder="Your name"
                      className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:ring-2 focus:ring-primary-400"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-xs font-semibold text-foreground-800 mb-1.5">
                      Phone / WhatsApp
                    </label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      required
                      placeholder="+91 00000 00000"
                      className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:ring-2 focus:ring-primary-400"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-xs font-semibold text-foreground-800 mb-1.5">
                    Email Address
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="you@example.com"
                    className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  />
                </div>

                <div>
                  <label htmlFor="topic" className="block text-xs font-semibold text-foreground-800 mb-1.5">
                    What can we help with?
                  </label>
                  <select
                    id="topic"
                    name="topic"
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400"
                  >
                    <option value="" disabled>Select a topic</option>
                    {TOPICS.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-xs font-semibold text-foreground-800 mb-1.5">
                    Message <span className="text-foreground-500 font-normal">(optional)</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    maxLength={500}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Tell us about your investment goals and preferred villa type..."
                    className="w-full rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
                  ></textarea>
                </div>

                <input
                  type="text"
                  name="contact_alt"
                  className="hp-field"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                {status === "error" && formError && (
                  <div className="rounded-lg bg-accent-100 border border-accent-300 px-4 py-3 text-sm text-accent-900">
                    <i className="ri-error-warning-line mr-1.5"></i>
                    {formError}
                  </div>
                )}

                <button
                  type="submit"
                  className="w-full inline-flex items-center justify-center gap-2 text-sm md:text-base font-semibold whitespace-nowrap rounded-full bg-primary-600 text-background-50 px-6 py-3.5 hover:bg-primary-700 transition-colors"
                >
                  Send Message
                  <i className="ri-arrow-right-line"></i>
                </button>
                <p className="text-center text-[11px] text-foreground-600">
                  We respect your privacy. Your details are only used to respond to your enquiry.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}